import { isEmpty } from "lodash";
import { useMemo } from "react";
import type { TokenAutomation } from "hooks/firestore/models/tokenAutomation";

import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import { useTokenAllocations } from "@/features/betslip/hooks/useTokenAllocations";
import type { TokenAllocations } from "@/features/betslip/hooks/useTokenAllocations";
import type { BetSelection } from "@/features/betslip/types";
import { useSortedTokens } from "@/features/betslip/hooks/useSortedTokens";

type ActionType = NonNullable<TokenAutomation["actions"]>[number]["type"];

type UseTokenPillsParams = {
  outcomeIds: string[];
  isMulti: boolean;
  selection?: BetSelection;
  isReviewingOrPlacing: boolean;
  isCardInactive: boolean;
};

export const useTokenPills = ({
  outcomeIds,
  isMulti,
  selection,
  isReviewingOrPlacing,
  isCardInactive,
}: UseTokenPillsParams) => {
  const outcomeId = outcomeIds[0];

  const { isLoading, allocations, automations, usedAllocations } =
    useTokenAllocations({ outcomeIds, isMulti });
  const { sortedAutomations, sortedAllocations } = useSortedTokens(
    allocations,
    automations,
  );

  const selectedAllocationId = useBetslipStore((state) =>
    isMulti ? state.tokens.multi : state.tokens.singles[outcomeId] ?? null,
  );
  const multiTokenAllocationId = useBetslipStore((state) => state.tokens.multi);
  const slipSelectionsCount = useBetslipStore(
    (state) => Object.keys(state.selections).length,
  );
  const setSingleAllocation = useBetslipStore(
    (state) => state.setSingleAllocation,
  );
  const setMultiAllocation = useBetslipStore(
    (state) => state.setMultiAllocation,
  );

  const isQuaddie = selection?.kind === "quaddie";
  const semLegs = selection?.kind === "sem" ? selection.legs ?? [] : [];
  const legCount = semLegs.length > 0 ? semLegs.length : slipSelectionsCount;

  const selectedAutomationType = useMemo<ActionType | undefined>(() => {
    const allocation = sortedAllocations.find(
      (a) => a.id === selectedAllocationId,
    );
    const automation = sortedAutomations.find(
      (a) => a.automationId === allocation?.automationId,
    );
    return automation?.actions?.[0]?.type;
  }, [selectedAllocationId, sortedAllocations, sortedAutomations]);

  const hasTokens = !isEmpty(sortedAllocations);
  const hasSelectedToken = !!selectedAllocationId;
  // A card takes a new token while its market is active, and it isn't reviewing
  // or placing. Show the pills when that's the case, or when a token is already
  // applied (kept visible so it can be deselected). Quaddies never take tokens.
  const canApplyToken = !isReviewingOrPlacing && !isCardInactive;
  const show = hasTokens && !isQuaddie && (canApplyToken || hasSelectedToken);

  const selectAllocation = (allocationId: string | null) => {
    if (isMulti) setMultiAllocation(allocationId);
    else setSingleAllocation(outcomeId, allocationId);
  };
  const releaseAllocation = (allocation: TokenAllocations[number]) => {
    if (isMulti) setMultiAllocation(null);
    else if (allocation.usedAtOutcomeId) {
      setSingleAllocation(allocation.usedAtOutcomeId, null);
    }
  };

  // Selecting a token first releases any prior claim it holds elsewhere, then
  // toggles it on this bet (a second tap on the same token clears it).
  const pickAllocation = (allocation: TokenAllocations[number]) => {
    releaseAllocation(allocation);
    selectAllocation(
      allocation.id === selectedAllocationId ? null : allocation.id,
    );
  };
  const deselectAllocation = () => selectAllocation(null);

  return {
    show,
    isLoading,
    selectionEventId: selection?.eventId ?? "",
    selectedAllocationId,
    selectedAutomationType,
    sortedAllocations,
    sortedAutomations,
    usedAllocations,
    legCount,
    multiTokenAllocationId,
    pickAllocation,
    deselectAllocation,
  };
};
